import React from 'react'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import { t } from 'ttag'

import { postCopyInterviewTemplate } from 'api/interviewTemplates'
import { useCreateInterviewTemplateModal } from 'components/modals/CreateInterviewTemplateModal/hooks'
import { toTemplatesCreator } from 'ducks/router'
import { useModal, useSearch, useSnackbar } from 'hooks'
import { interviewTemplates } from 'orm'

const DEFAULT_TEMPLATE_STATE = { id: null, title: null }

const useMutations = () => {
  const dispatch = useDispatch()

  const handleDeleteInterviewTemplate = (
    { templateId },
    { postSuccessAction } = {},
  ) => {
    dispatch(
      interviewTemplates.deleteStart({
        message: t`Interview template was successfully deleted.`,
        pk: R.toString(templateId),
        postSuccessAction,
      }),
    )
  }

  const handlePostCopyInterviewTemplate = async ({ title, department, uuid }) =>
    await postCopyInterviewTemplate({ title, department, uuid })

  return {
    handleDeleteInterviewTemplate,
    handlePostCopyInterviewTemplate,
  }
}

const useDeleteInformationModal = () => {
  const { handleClose, handleOpen, isOpen } = useModal()

  return {
    handleDeleteInformationClose: handleClose,
    handleDeleteInformationOpen: handleOpen,
    isDeleteInformationOpen: isOpen,
  }
}

const useEditInformationModal = () => {
  const { handleClose, handleOpen, isOpen } = useModal()

  return {
    handleEditInformationClose: handleClose,
    handleEditInformationOpen: handleOpen,
    isEditInformationOpen: isOpen,
  }
}

const useCopyInterviewTemplateModal = (state, handlers) => {
  const { templateId } = state
  const {
    handleInterviewTemplateRedirect,
    handlePostCopyInterviewTemplate,
    handleResetTemplateId,
    handleShowSnackbar,
  } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleCopyInterviewTemplateClose = React.useCallback(() => {
    handleClose()
    handleResetTemplateId()
  }, [handleClose, handleResetTemplateId])

  const handleCopyInterviewTemplateConfirm = React.useCallback(
    async ({ title, department }, { onError } = {}) => {
      const { error, meta, response } = await handlePostCopyInterviewTemplate({
        title,
        department,
        uuid: templateId,
      })

      if (response) {
        handleClose()
        handleInterviewTemplateRedirect({ uuid: response?.id })
        handleShowSnackbar({
          message: t`Interview template was successfully copied.`,
          type: 'success',
        })
      } else {
        onError({ message: error, type: meta.HTTPstatus })
      }
    },
    [
      templateId,
      handleClose,
      handleInterviewTemplateRedirect,
      handlePostCopyInterviewTemplate,
      handleShowSnackbar,
    ],
  )

  return {
    handleCopyInterviewTemplateClose,
    handleCopyInterviewTemplateConfirm,
    handleCopyInterviewTemplateOpen: handleOpen,
    isCopyInterviewTemplateOpen: isOpen,
  }
}

const useDeleteInterviewTemplateModal = (state, handlers) => {
  const { templateId } = state
  const { handleDeleteInterviewTemplate, handleResetTemplateId } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleDeleteInterviewTemplateClose = React.useCallback(() => {
    handleClose()
    handleResetTemplateId()
  }, [handleClose, handleResetTemplateId])

  const handleDeleteInterviewTemplateConfirm = React.useCallback(() => {
    handleDeleteInterviewTemplate(
      { templateId },
      {
        postSuccessAction: () => {
          handleDeleteInterviewTemplateClose()
        },
      },
    )
  }, [
    templateId,
    handleDeleteInterviewTemplate,
    handleDeleteInterviewTemplateClose,
  ])

  return {
    handleDeleteInterviewTemplateClose,
    handleDeleteInterviewTemplateConfirm,
    handleDeleteInterviewTemplateOpen: handleOpen,
    isDeleteInterviewTemplateOpen: isOpen,
  }
}

export const useFilter = () => {
  const [filter, setFilter] = React.useState({ department: [] })
  const [departmentValue, setDepartmentValue] = React.useState([])

  const setDepartment = department => {
    setFilter({ department: department.map(dep => dep.value) })
    setDepartmentValue(department)
  }

  return {
    state: { filter, departmentValue },
    handlers: { setDepartment },
  }
}

export const useInterviewTemplates = () => {
  const dispatch = useDispatch()

  const [template, setTemplate] = React.useState(DEFAULT_TEMPLATE_STATE)

  const handleInterviewTemplateRedirect = React.useCallback(
    ({ uuid }) => {
      dispatch(toTemplatesCreator({ tab: 'interviews', uuid }))
    },
    [dispatch],
  )

  const handleResetTemplateId = () => setTemplate(DEFAULT_TEMPLATE_STATE)

  const [search, setSearch] = useSearch()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleDeleteInterviewTemplate, handlePostCopyInterviewTemplate } =
    useMutations()

  const {
    handleCreateInteviewTemplateClose,
    handleCreateInterviewTemplateConfirm,
    handleCreateInteviewTemplateOpen,
    isCreateInterviewTemplateOpen,
  } = useCreateInterviewTemplateModal()

  const {
    handleCopyInterviewTemplateClose,
    handleCopyInterviewTemplateConfirm,
    handleCopyInterviewTemplateOpen,
    isCopyInterviewTemplateOpen,
  } = useCopyInterviewTemplateModal(
    { templateId: template?.id },
    {
      handleInterviewTemplateRedirect,
      handlePostCopyInterviewTemplate,
      handleResetTemplateId,
      handleShowSnackbar,
    },
  )

  const {
    handleDeleteInterviewTemplateClose,
    handleDeleteInterviewTemplateConfirm,
    handleDeleteInterviewTemplateOpen,
    isDeleteInterviewTemplateOpen,
  } = useDeleteInterviewTemplateModal(
    { templateId: template?.id },
    { handleDeleteInterviewTemplate, handleResetTemplateId },
  )

  const {
    handleEditInformationClose,
    handleEditInformationOpen,
    isEditInformationOpen,
  } = useEditInformationModal()

  const {
    handleDeleteInformationClose,
    handleDeleteInformationOpen,
    isDeleteInformationOpen,
  } = useDeleteInformationModal()

  const {
    state: { filter, departmentValue },
    handlers: { setDepartment },
  } = useFilter()

  const handleEdit = React.useCallback(
    ({ id, writeable }) => {
      if (!writeable) {
        handleEditInformationOpen()

        return
      }

      handleInterviewTemplateRedirect({ uuid: id })
    },
    [handleEditInformationOpen, handleInterviewTemplateRedirect],
  )

  const handleCopy = React.useCallback(
    ({ id, title, department }) => {
      setTemplate(R.mergeLeft({ id, title, department }))
      handleCopyInterviewTemplateOpen()
    },
    [handleCopyInterviewTemplateOpen],
  )

  const handleDelete = React.useCallback(
    ({ id, writeable }) => {
      if (!writeable) {
        handleDeleteInformationOpen()

        return
      }

      setTemplate(R.mergeLeft({ id }))
      handleDeleteInterviewTemplateOpen()
    },
    [handleDeleteInformationOpen, handleDeleteInterviewTemplateOpen],
  )

  return {
    state: {
      search,
      template,
      isCopyInterviewTemplateOpen,
      isCreateInterviewTemplateOpen,
      isDeleteInterviewTemplateOpen,
      isDeleteInformationOpen,
      isEditInformationOpen,
      departmentValue,
      filter,
    },
    handlers: {
      handleCopyInterviewTemplateClose,
      handleCopyInterviewTemplateConfirm,
      handleCreateInteviewTemplateClose,
      handleCreateInterviewTemplateConfirm,
      handleCreateInteviewTemplateOpen,
      handleDeleteInterviewTemplateClose,
      handleDeleteInterviewTemplateConfirm,
      handleDeleteInformationClose,
      handleEditInformationClose,
      setDepartment,
      handleCopy,
      handleDelete,
      handleEdit,
      setSearch,
    },
  }
}
